// Stable Router that can be used in useEffect
// https://github.com/vercel/next.js/issues/18127#issuecomment-1619721701
import Router, { useRouter } from 'next/router';
import {
  Dispatch, SetStateAction, useEffect, useRef,
} from 'react';
import { buildSearchParams, buildObjectFromSearchParams } from '@utils/data-fetching/searchParams';
import { ObjectFilterParams } from '@utils/infinite-scrolling/processedServerSideQueries';
import { ParsedUrlQuery } from 'querystring';
import { getJobListParametersFromSlug, buildJobListUrl } from '@utils/job';
import { TFunction } from 'next-i18next';
import { useRouterLocale } from '@hooks/common/useRouterLocale';
import { InfinitePageType } from '@type/swr';


/**
 * @description This hook is used to update the browser history and browser navbar URL with the current page number
 * and search params. Just feed it the current page number and search params and it will do the rest.
 * @param pageInView - *string | number* - the current page in view
 * @param searchParams - *object* - the current search params
 * @param setSearchParams - *function* - the function (state setter) to update the search params. If you don't want to update
 * the search params, just don't pass this parameter.
 * @param source - *string* - the page where this hook is used.
 * @param t - *TFunction - the translation function.
 * @param paginationType - *InfinitePageType* - the type of pagination used. Default is 'page'.
 *
 * @example
 * // Use it like this in the component:
 * usePaginationHistory(pageInView, searchParams, true);
 */
export const useBrowserHistoryAndURL = (
  pageInView: string | number | undefined,
  searchParams: ObjectFilterParams | object | undefined,
  setSearchParams?: Dispatch<SetStateAction<object>>,
  source?: 'jobList',
  t?: TFunction,
  paginationType: InfinitePageType = 'page',
) => {
  const router = useRouter();
  const lastSearchParamsRef = useRef<string | null>('');
  const keepInitialQueryParams = useRef<boolean>(true);

  // Locale
  const locale = useRouterLocale();

  // Update the browser history and URL
  useEffect(() => {
    // Get query params from router query
    // ************************************
    const {
      pageNr: pageNrQuery,
      jobSlug: jobSlugQuery,
      locale: localeQuery,
      ...restQueryParams
    } = router.query;

    // Extract page number/cursor from query param.
    // If using catch-all routes ([[...pageNr]]), the query param is an array.
    // ************************************
    let pageNrQueryValue: string | number = paginationType === 'cursor' ? '' : 1;

    if (paginationType === 'cursor') {
      pageNrQueryValue = Array.isArray(pageNrQuery) ? pageNrQuery[0] : (pageNrQuery ?? '');
    } else if (Array.isArray(pageNrQuery)) {
      pageNrQueryValue = parseInt(pageNrQuery[0], 10);
    } else if (typeof pageNrQuery !== 'undefined') {
      pageNrQueryValue = parseInt(pageNrQuery, 10);
    }

    // Build the search params
    const urlParams = buildSearchParams(searchParams, '');

    if (urlParams !== lastSearchParamsRef.current) {
      const newQueryWithAllParams: ParsedUrlQuery = {};
      if (typeof pageNrQuery !== 'undefined') {
        newQueryWithAllParams.pageNr = pageNrQuery;
      }
      if (typeof jobSlugQuery !== 'undefined') {
        newQueryWithAllParams.jobSlug = jobSlugQuery;
      }
      if (typeof localeQuery !== 'undefined') {
        newQueryWithAllParams.locale = localeQuery;
      }

      const newQuery = `${urlParams}${buildSearchParams(newQueryWithAllParams, '&')}`;
      lastSearchParamsRef.current = urlParams;

      if (source === 'jobList' && t) {
        const urlSearchParams = keepInitialQueryParams.current ? window.location.search : '';
        const jobListUrl = buildJobListUrl(t, locale, searchParams) + urlSearchParams;
        void Router.push({ query: newQuery }, jobListUrl, { shallow: true });
      } else {
        void Router.push({ query: newQuery }, undefined, { shallow: true });
      }
    }

    // Update the URL if necessary (for pageNr/cursor, we always use router `.replace`).
    // If page number is 1, we need to remove it from URL
    // ************************************
    if (
      (pageInView && paginationType === 'page' && (pageInView !== pageNrQueryValue || (pageInView === 1 && typeof pageNrQuery !== 'undefined')))
        || (paginationType === 'cursor' && pageInView !== pageNrQueryValue)
    ) {
      const newQueryWithPageNr = { ...restQueryParams };

      if (paginationType === 'cursor') {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      } else if (Number(pageInView) > 1) {
        newQueryWithPageNr.pageNr = pageInView?.toString();
      }

      if (typeof jobSlugQuery !== 'undefined') {
        newQueryWithPageNr.jobSlug = jobSlugQuery;
      }

      if (typeof localeQuery !== 'undefined') {
        newQueryWithPageNr.locale = localeQuery;
      }

      if (source === 'jobList' && t) {
        const jobListUrl = buildJobListUrl(t, locale, searchParams);

        void router.replace({ query: newQueryWithPageNr }, jobListUrl, { shallow: true });
      } else {
        void router.replace({ query: newQueryWithPageNr }, undefined, { shallow: true });
      }
    }

    keepInitialQueryParams.current = false;
  }, [pageInView, searchParams, router, t, source, locale, paginationType]);


  // Update the search params when history back/forward actions are triggered
  useEffect(() => {
    router.beforePopState(({ as }) => {
      const currentPath = router.asPath;
      // Will run when leaving the current page - shallow routing; on back/forward actions
      if (as !== currentPath && setSearchParams) {
        const currentSearchParams = window.location.search;
        const currentParamsObj = buildObjectFromSearchParams(currentSearchParams) || {};
        setSearchParams(currentParamsObj);
      }
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router, setSearchParams]);


  useEffect(() => {
    const handleRouteChangeComplete = (url: string, { shallow }: { shallow: boolean }) => {
      if (!shallow && setSearchParams && t) {
        const newSearchParams = getJobListParametersFromSlug(router.query.slug, t);
        lastSearchParamsRef.current = buildSearchParams(newSearchParams, '');
        setSearchParams(newSearchParams);
      }
    };

    if (source === 'jobList') {
      router.events.on('routeChangeComplete', handleRouteChangeComplete);
    }

    // Cleanup the event listener on component unmount
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events, router.query, setSearchParams, source, t]);
};
